import { useEffect, useMemo, useRef, useState } from 'react';
import { Info, TriangleAlert, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TrashIcon } from '@/components/icons';
import MaasModelSelect from '@/components/MaasModelSelect';
import type { ModelGroup } from '@/utils/model-display';
import { maasApi } from '@/api/maas.api';
import { useMaasCredentials } from '@/pages/credentials/hooks/useMaasCredentials';
import { useTenantFailover } from '@/pages/credentials/hooks/useTenantFailovers';
import type { MaasModel } from '@/proto/agentplatform/v2/maas_pb';
import { type FaultToleranceFormState, type StepTypePolicyFormState } from './agentFormTypes';

interface Props {
  value: FaultToleranceFormState;
  onChange: (v: FaultToleranceFormState) => void;
  /** 备用模型下拉选项（来自当前凭证的模型列表） */
  groupedModels?: ModelGroup[];
  /** 是否展示「开放给 Agent 实例单独配置」开关（仅模板侧） */
  showConfigurable?: boolean;
  /** 隐藏「启用容错配置」总开关（实例侧：是否启用容错继承自模板，不可在实例上修改） */
  hideEnabledToggle?: boolean;
}

/** 原型一致的原生开关 */
function Toggle({
  id,
  checked,
  onClick,
  disabled,
}: {
  id?: string;
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

/** 带边框的分区卡片：头部标题 + 右侧开关，主体带上分隔线 */
function ToggleCard({
  title,
  checked,
  onToggle,
  disabled,
  children,
}: {
  title: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-input">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <span className="text-sm font-semibold">{title}</span>
        <Toggle checked={checked} onClick={onToggle} disabled={disabled} />
      </div>
      {checked && children && <div className="border-t border-input px-4 py-4">{children}</div>}
    </div>
  );
}

/** 必填标签（红色星号前缀） */
function ReqLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-sm font-medium">
      {required && <span className="mr-1 text-destructive">*</span>}
      {children}
    </Label>
  );
}

/** 复选项（步骤类型选择用） */
function CheckboxItem({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-input accent-primary"
      />
      <span className="text-sm">{label}</span>
    </label>
  );
}

/** 数值输入（1-10 之类），返回整数；error 非空时红框 + 行内错误提示 */
function NumField({
  label,
  value,
  onChange,
  min,
  max,
  disabled,
  required,
  extra,
  error,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  required?: boolean;
  extra?: string;
  error?: string;
}) {
  return (
    <div className="flex-1 space-y-1.5">
      <ReqLabel required={required}>{label}</ReqLabel>
      <Input
        type="number"
        min={min}
        max={max}
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        className={`h-10 ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
      />
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        extra && <p className="text-xs text-muted-foreground">{extra}</p>
      )}
    </div>
  );
}

/** 时长输入：数值 + 单位下拉（秒/分钟/小时），内部统一以秒存储；error 非空时红框 + 行内错误提示 */
function DurationField({
  label,
  seconds,
  onChange,
  units,
  disabled,
  required,
  extra,
  error,
}: {
  label: string;
  seconds: number;
  onChange: (secs: number) => void;
  units: Array<{ value: number; label: string }>;
  disabled?: boolean;
  required?: boolean;
  extra?: string;
  error?: string;
}) {
  // 初始/外部回填时选一个能整除的最大单位展示；之后单位仅在用户主动切换时改变，
  // 避免清空数字（seconds=0）时单位自动跳回最小单位。
  const pickUnit = (secs: number) =>
    [...units].reverse().find((u) => secs > 0 && secs % u.value === 0) ?? units[0];
  const [unitValue, setUnitValue] = useState(() => pickUnit(seconds).value);
  // 记录本组件自己发出的秒数，区分「用户输入」与「外部回填」：前者不重推单位。
  const lastEmitted = useRef(seconds);
  useEffect(() => {
    if (lastEmitted.current === seconds) return;
    lastEmitted.current = seconds;
    setUnitValue(pickUnit(seconds).value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);
  const unit = units.find((u) => u.value === unitValue) ?? units[0];
  const display = seconds > 0 ? seconds / unit.value : 0;
  const emit = (secs: number) => {
    lastEmitted.current = secs;
    onChange(secs);
  };
  return (
    <div className="flex-1 space-y-1.5">
      <ReqLabel required={required}>{label}</ReqLabel>
      <div className="flex gap-2">
        <Input
          type="number"
          min={1}
          disabled={disabled}
          value={display || ''}
          onChange={(e) => emit((parseInt(e.target.value) || 0) * unit.value)}
          className={`h-10 flex-1 ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
        />
        <Select
          value={String(unit.value)}
          onValueChange={(val) => {
            const next = parseInt(val);
            setUnitValue(next);
            emit(display > 0 ? display * next : 0);
          }}
        >
          <SelectTrigger className="h-10 w-24" disabled={disabled}>
            <SelectValue>{unit.label}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {units.map((u) => (
              <SelectItem key={u.value} value={String(u.value)}>
                {u.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        extra && <p className="text-xs text-muted-foreground">{extra}</p>
      )}
    </div>
  );
}

/** 重试次数/间隔的行内校验：1-10，超出返回错误文案（与 validateFaultTolerance 口径一致） */
function retryRangeError(n: number): string | undefined {
  return n >= 1 && n <= 10 ? undefined : '必须为 1-10 的整数';
}

/** 时长的行内校验：正整数且不超过 maxSeconds */
function durationError(seconds: number, maxSeconds: number, maxLabel: string): string | undefined {
  return seconds > 0 && seconds <= maxSeconds ? undefined : `必须为正整数且不超过 ${maxLabel}`;
}

const STEP_UNITS = [
  { value: 1, label: '秒' },
  { value: 60, label: '分钟' },
  { value: 3600, label: '小时' },
];
const TASK_UNITS = [
  { value: 60, label: '分钟' },
  { value: 3600, label: '小时' },
];

/** 内联提示条（复刻原型 info / warning banner，无现成 Alert 组件） */
function InlineAlert({
  type,
  message,
  description,
}: {
  type: 'info' | 'warning';
  message: string;
  description?: string;
}) {
  const Icon = type === 'warning' ? TriangleAlert : Info;
  const tone =
    type === 'warning'
      ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
      : 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300';
  return (
    <div className={`flex gap-2 rounded-md border px-3 py-2 ${tone}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-0.5">
        <p className="text-sm">{message}</p>
        {description && <p className="text-xs opacity-90">{description}</p>}
      </div>
    </div>
  );
}

/** 已选备用模型的有序列表（上移 / 下移 / 移除），两种模式共用 */
function OrderedModelList({
  selected,
  onChange,
  disabled,
}: {
  selected: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
}) {
  const move = (idx: number, delta: number) => {
    const next = [...selected];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  if (selected.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {selected.map((name, idx) => (
        <div
          key={name}
          className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5"
        >
          <span className="text-xs font-mono text-muted-foreground w-5 shrink-0">{idx + 1}</span>
          <span className="flex-1 truncate text-sm">{name}</span>
          <button
            type="button"
            disabled={disabled || idx === 0}
            onClick={() => move(idx, -1)}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed px-1"
            aria-label="上移"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={disabled || idx === selected.length - 1}
            onClick={() => move(idx, 1)}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed px-1"
            aria-label="下移"
          >
            ↓
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(selected.filter((n) => n !== name))}
            className="text-muted-foreground hover:text-destructive px-1"
            aria-label="移除"
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function parsePool(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 去重保序 */
function uniq(list: string[]): string[] {
  return Array.from(new Set(list));
}

/**
 * 有序备用模型选择器。
 * - 租户已限制（TenantFailover 启用且池非空）：备用模型只能取自租户池；文本框 onBlur 自动清除池外模型。
 * - 租户未限制：允许当前 Agent 自定义——凭证探测 +「获取模型」+ 逗号分隔文本框自由编辑。
 */
function BackupModelPicker({
  value,
  onChange,
  groupedModels,
  tenantRestricted,
  tenantPool,
  disabled,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  groupedModels?: ModelGroup[];
  tenantRestricted: boolean;
  tenantPool: string[];
  disabled?: boolean;
}) {
  const selected = value || [];

  // 租户已限制时，剔除已选中但不在池内的模型（存量模板 / 租户后置限制场景）。
  useEffect(() => {
    if (!tenantRestricted) return;
    const pruned = selected.filter((m) => tenantPool.includes(m));
    if (pruned.length !== selected.length) onChange(pruned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantRestricted, tenantPool.join(','), selected.join(',')]);

  // 文本框本地态：编辑中不即时回写，onBlur 时统一解析（未限制态可保留自定义模型）。
  const [poolText, setPoolText] = useState(selected.join(', '));
  useEffect(() => {
    setPoolText(selected.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.join(',')]);

  // 未限制态：凭证探测可用模型列表
  const { data: credData } = useMaasCredentials(1, 100);
  const managedCreds = useMemo(() => credData?.items ?? [], [credData?.items]);
  const [sourceCredId, setSourceCredId] = useState<string | undefined>(undefined);
  const [probeModels, setProbeModels] = useState<MaasModel[]>([]);
  const [probing, setProbing] = useState(false);

  async function onProbe() {
    if (!sourceCredId) {
      toast.info('先选择一个来源凭证');
      return;
    }
    setProbing(true);
    try {
      const res = await maasApi.listModelsByCredential(sourceCredId);
      setProbeModels(res.items);
      toast.success(`拿到 ${res.items.length} 个模型`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '获取模型失败');
    } finally {
      setProbing(false);
    }
  }

  // 租户已限制：下拉候选 = 池内未选项
  const restrictedRemaining = tenantPool.filter((m) => !selected.includes(m));

  // 未限制：下拉候选 = 当前 Agent 凭证模型 ∪ 探测模型，去掉已选
  const probeGroup: ModelGroup[] =
    probeModels.length > 0 ? [{ label: '探测模型', models: probeModels }] : [];
  const customRemaining = [...(groupedModels || []), ...probeGroup]
    .map((g) => ({ ...g, models: g.models.filter((m) => !selected.includes(m.name)) }))
    .filter((g) => g.models.length > 0);

  const commitText = () => {
    let next = uniq(parsePool(poolText));
    if (tenantRestricted) next = next.filter((m) => tenantPool.includes(m));
    onChange(next);
    setPoolText(next.join(', '));
  };

  return (
    <div className="space-y-3">
      <InlineAlert
        type={tenantRestricted ? 'warning' : 'info'}
        message={
          tenantRestricted
            ? '租户主账号已限制可选模型范围'
            : '租户未限制模型范围，可由当前 Agent 自定义'
        }
        description="运行时所有备用模型都使用「当前 Agent 模型凭证」的 API Key。"
      />

      {selected.length === 0 && (
        <InlineAlert type="warning" message="开启模型降级切换后必须至少选择一个备用模型" />
      )}

      <OrderedModelList selected={selected} onChange={onChange} disabled={disabled} />

      {/* 未限制态：凭证探测「获取模型列表」 */}
      {!tenantRestricted && (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">获取模型列表</Label>
          <div className="flex gap-2">
            <Select
              value={sourceCredId ?? ''}
              onValueChange={(v) => setSourceCredId(v || undefined)}
            >
              <SelectTrigger className="h-10 flex-1" disabled={disabled}>
                <SelectValue placeholder="选择已托管的模型凭证" />
              </SelectTrigger>
              <SelectContent>
                {managedCreds.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} · {c.defaultModel || '未设默认模型'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              className="h-10 shrink-0"
              disabled={disabled || probing}
              onClick={onProbe}
            >
              <Zap className="mr-1 h-3.5 w-3.5" />
              获取模型
            </Button>
          </div>
        </div>
      )}

      {/* 下拉添加：租户已限制取自池，未限制取自凭证/探测模型 */}
      {tenantRestricted ? (
        <Select value="" onValueChange={(val) => val && onChange(uniq([...selected, val]))}>
          <SelectTrigger className="h-10" disabled={disabled || restrictedRemaining.length === 0}>
            <SelectValue
              placeholder={restrictedRemaining.length === 0 ? '已全部添加' : '添加备用模型…'}
            />
          </SelectTrigger>
          <SelectContent searchable searchPlaceholder="搜索模型...">
            <SelectGroup>
              <SelectLabel>租户 Failover 池</SelectLabel>
              {restrictedRemaining.map((name) => (
                <SelectItem key={name} value={name} searchKeywords={name}>
                  <span className="truncate">{name}</span>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      ) : (
        (customRemaining.length > 0 || (groupedModels && groupedModels.length > 0)) && (
          <MaasModelSelect
            groups={customRemaining}
            value=""
            onChange={(val) => val && onChange(uniq([...selected, val]))}
            placeholder={customRemaining.length === 0 ? '已全部添加' : '添加备用模型…'}
            disabled={disabled || customRemaining.length === 0}
            triggerClassName="h-10"
          />
        )
      )}

      {/* 逗号分隔文本框：请求顺序可直接手动编辑 */}
      <div className="space-y-1.5">
        <ReqLabel required>Failover 备用模型与请求顺序</ReqLabel>
        <Textarea
          rows={3}
          disabled={disabled}
          value={poolText}
          placeholder="deepseek-v3.1, qwen3-235b-a22b"
          onChange={(e) => setPoolText(e.target.value)}
          onBlur={commitText}
          className={
            selected.length === 0 ? 'border-destructive focus-visible:ring-destructive' : undefined
          }
        />
        {selected.length === 0 && (
          <p className="text-xs text-destructive">请至少选择一个备用模型</p>
        )}
        <p className="text-xs text-muted-foreground">
          {tenantRestricted
            ? '按英文逗号分隔，从左到右依次请求；可直接手动编辑，超出当前模型范围的模型在保存后会被自动清除，请勿添加新模型。'
            : '按英文逗号分隔，从左到右依次请求；可直接手动编辑。'}
        </p>
      </div>
    </div>
  );
}

/** 单个步骤类型策略卡片（嵌套在「步骤级容错」内，勾选步骤类型后展开） */
function StepPolicyBlock({
  label,
  policy,
  onChange,
  isModel,
  groupedModels,
  tenantRestricted,
  tenantPool,
}: {
  label: string;
  policy: StepTypePolicyFormState;
  onChange: (p: StepTypePolicyFormState) => void;
  isModel?: boolean;
  groupedModels?: ModelGroup[];
  tenantRestricted?: boolean;
  tenantPool?: string[];
}) {
  const patch = (partial: Partial<StepTypePolicyFormState>) => onChange({ ...policy, ...partial });
  return (
    <div className="rounded-md border border-input">
      <div className="border-b border-input px-4 py-2.5 text-sm font-medium">{label}</div>
      <div className="space-y-4 px-4 py-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DurationField
            label="单次步骤超时"
            required
            seconds={policy.attemptTimeoutSeconds}
            onChange={(s) => patch({ attemptTimeoutSeconds: s })}
            units={STEP_UNITS}
            error={durationError(policy.attemptTimeoutSeconds, 259200, '72 小时')}
          />
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">步骤自动重试</Label>
            <div>
              <Toggle
                checked={policy.retryEnabled}
                onClick={() => {
                  const next = !policy.retryEnabled;
                  // 开关与重试次数联动：关闭时次数清零（后端以 max_retries>0 判定自动重试），
                  // 开启时恢复一个有效次数；关闭同时失效模型 Failover。
                  patch(
                    next
                      ? { retryEnabled: true, maxRetries: policy.maxRetries || 3 }
                      : { retryEnabled: false, maxRetries: 0, failoverEnabled: false }
                  );
                }}
              />
            </div>
          </div>
        </div>
        {policy.retryEnabled && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumField
              label="最大重试次数 (1-10)"
              value={policy.maxRetries}
              onChange={(n) => patch({ maxRetries: n, retryEnabled: n > 0 || policy.retryEnabled })}
              min={1}
              max={10}
              error={retryRangeError(policy.maxRetries)}
            />
            <NumField
              label="重试间隔（秒，1-10）"
              value={policy.retryIntervalSeconds}
              onChange={(n) => patch({ retryIntervalSeconds: n })}
              min={1}
              max={10}
              error={retryRangeError(policy.retryIntervalSeconds)}
            />
          </div>
        )}
        {isModel && (
          <div className="space-y-3 border-t border-input pt-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label className="text-sm font-medium">模型降级切换</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  按顺序请求备用模型；配额超过路线数时循环回默认模型。
                </p>
              </div>
              <Toggle
                checked={policy.failoverEnabled}
                disabled={!policy.retryEnabled}
                onClick={() => patch({ failoverEnabled: !policy.failoverEnabled })}
              />
            </div>
            {!policy.retryEnabled && (
              <p className="text-xs text-muted-foreground">
                模型 Failover 仅可在模型调用步骤开启自动重试后启用。
              </p>
            )}
            {policy.retryEnabled && policy.failoverEnabled && (
              <BackupModelPicker
                value={policy.backupModels}
                onChange={(v) => patch({ backupModels: v })}
                groupedModels={groupedModels}
                tenantRestricted={!!tenantRestricted}
                tenantPool={tenantPool ?? []}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** 容错配置编辑器主体，模板表单与实例覆盖共用 */
export default function FaultToleranceFields({
  value,
  onChange,
  groupedModels,
  showConfigurable,
  hideEnabledToggle,
}: Props) {
  const patch = (partial: Partial<FaultToleranceFormState>) => onChange({ ...value, ...partial });

  const { data: tenantFailover } = useTenantFailover();
  const tenantPool = tenantFailover?.backupModels ?? [];
  const tenantRestricted = !!tenantFailover?.enabled && tenantPool.length > 0;

  return (
    <div className="space-y-4">
      {/* 容错总开关（实例侧隐藏：是否启用容错继承自模板） */}
      {!hideEnabledToggle && (
        <div className="rounded-lg border border-input">
          <div className="flex items-start justify-between gap-4 px-4 py-3">
            <div>
              <div className="text-sm font-semibold">启用容错配置</div>
              <p className="mt-1 text-xs text-muted-foreground">
                关闭后不执行自定义任务重试、步骤重试和模型 Failover；平台基础容错仍生效。
              </p>
            </div>
            <Toggle checked={value.enabled} onClick={() => patch({ enabled: !value.enabled })} />
          </div>
        </div>
      )}

      {hideEnabledToggle && !value.enabled && (
        <p className="text-sm text-muted-foreground">模板未启用容错配置，本实例无可配置项。</p>
      )}

      {value.enabled && (
        <div className="space-y-4">
          {/* 任务级容错 */}
          <ToggleCard
            title="任务级容错"
            checked={value.taskEnabled}
            onToggle={() => patch({ taskEnabled: !value.taskEnabled })}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DurationField
                  label="任务总超时时间"
                  required
                  extra="覆盖全部 Run，最长 168 小时"
                  seconds={value.taskTotalTimeoutSeconds}
                  onChange={(s) => patch({ taskTotalTimeoutSeconds: s })}
                  units={TASK_UNITS}
                  error={durationError(value.taskTotalTimeoutSeconds, 604800, '168 小时')}
                />
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">任务自动重试</Label>
                  <div>
                    <Toggle
                      checked={value.taskRetryEnabled}
                      onClick={() => {
                        const next = !value.taskRetryEnabled;
                        // 开关与重试次数联动：关闭时次数清零，开启时恢复一个有效次数。
                        patch(
                          next
                            ? { taskRetryEnabled: true, taskMaxRetries: value.taskMaxRetries || 3 }
                            : { taskRetryEnabled: false, taskMaxRetries: 0 }
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
              {value.taskRetryEnabled && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <NumField
                    label="任务最大重试次数 (1-10)"
                    value={value.taskMaxRetries}
                    onChange={(n) =>
                      patch({
                        taskMaxRetries: n,
                        taskRetryEnabled: n > 0 || value.taskRetryEnabled,
                      })
                    }
                    min={1}
                    max={10}
                    error={retryRangeError(value.taskMaxRetries)}
                  />
                  <NumField
                    label="任务重试间隔（秒，1-10）"
                    value={value.taskRetryIntervalSeconds}
                    onChange={(n) => patch({ taskRetryIntervalSeconds: n })}
                    min={1}
                    max={10}
                    error={retryRangeError(value.taskRetryIntervalSeconds)}
                  />
                </div>
              )}
            </div>
          </ToggleCard>

          {/* 步骤级容错 */}
          <ToggleCard
            title="步骤级容错"
            checked={value.stepEnabled}
            onToggle={() => patch({ stepEnabled: !value.stepEnabled })}
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <ReqLabel required>步骤类型</ReqLabel>
                <p className="text-xs text-muted-foreground">每种类型独立计算超时与重试次数。</p>
                <div className="flex flex-wrap items-center gap-6 pt-1">
                  <CheckboxItem
                    label="模型调用"
                    checked={value.model.selected}
                    onChange={(v) => patch({ model: { ...value.model, selected: v } })}
                  />
                  <CheckboxItem
                    label="工具调用"
                    checked={value.tool.selected}
                    onChange={(v) => patch({ tool: { ...value.tool, selected: v } })}
                  />
                </div>
              </div>

              {value.model.selected && (
                <StepPolicyBlock
                  label="模型调用"
                  policy={value.model}
                  onChange={(p) => patch({ model: p })}
                  isModel
                  groupedModels={groupedModels}
                  tenantRestricted={tenantRestricted}
                  tenantPool={tenantPool}
                />
              )}
              {value.tool.selected && (
                <StepPolicyBlock
                  label="工具调用"
                  policy={value.tool}
                  onChange={(p) => patch({ tool: p })}
                />
              )}
            </div>
          </ToggleCard>

          {/* 开放给实例单独配置 */}
          {showConfigurable && (
            <div className="rounded-lg border border-input">
              <div className="flex items-start justify-between gap-4 px-4 py-3">
                <div>
                  <div className="text-sm font-semibold">开放给 Agent 实例单独配置</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    开启后，所有关联实例可选择继承模板或覆盖整份容错配置。
                  </p>
                </div>
                <Toggle
                  checked={value.configurable}
                  onClick={() => patch({ configurable: !value.configurable })}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
